import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect } from "react";

import { activeChatSessionIdAtom } from "../atoms";

/**
 * Atom to track chat session IDs that have unread responses.
 * A session becomes "unread" when:
 * - The AI finishes responding (streaming -> complete)
 * - AND the user is not currently viewing that session
 *
 * A session becomes "read" when:
 * - The user navigates to/views that session
 */
export const unreadSessionIdsAtom = atom<Set<string>>(new Set<string>());

/**
 * Hook to manage unread session state.
 */
export const useUnreadSessions = () => {
  const [unreadSessionIds, setUnreadSessionIds] = useAtom(unreadSessionIdsAtom);
  const activeChatSessionId = useAtomValue(activeChatSessionIdAtom);

  const markSessionAsUnread = useCallback(
    (sessionId: string) => {
      setUnreadSessionIds((prev: Set<string>) => {
        const next = new Set(prev);
        next.add(sessionId);
        return next;
      });
    },
    [setUnreadSessionIds]
  );

  const markSessionAsRead = useCallback(
    (sessionId: string) => {
      setUnreadSessionIds((prev: Set<string>) => {
        if (!prev.has(sessionId)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(sessionId);
        return next;
      });
    },
    [setUnreadSessionIds]
  );

  const isSessionUnread = useCallback(
    (sessionId: string) => {
      return unreadSessionIds.has(sessionId);
    },
    [unreadSessionIds]
  );

  return {
    unreadSessionIds,
    activeChatSessionId,
    markSessionAsUnread,
    markSessionAsRead,
    isSessionUnread,
  };
};

/**
 * Hook to track the active chat session and clear unread status when viewed.
 * Should be used in the component that renders the chat session.
 */
export const useTrackActiveChatSession = (sessionId: string | undefined) => {
  const setActiveChatSessionId = useSetAtom(activeChatSessionIdAtom);
  const setUnreadSessionIds = useSetAtom(unreadSessionIdsAtom);

  useEffect(() => {
    if (!sessionId) {
      return;
    }

    // Set this session as active
    setActiveChatSessionId(sessionId);

    // Clear unread status since user is now viewing this session
    setUnreadSessionIds((prev: Set<string>) => {
      if (!prev.has(sessionId)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(sessionId);
      return next;
    });

    return () => {
      // Clear active session when unmounting
      setActiveChatSessionId((current) => (current === sessionId ? null : current));
    };
  }, [sessionId, setActiveChatSessionId, setUnreadSessionIds]);
};
